import { useContext, useEffect, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Card, Heading, Icon, Image, Paragraph, Text, TextButton, Upsell } from '@zillow/constellation-10';
import {
  IconHandshakeFilled,
  IconMailFilled,
  IconPiggyBankFilled,
  IconYardSignFilled,
} from '@zillow/constellation-icons';

import { logError, useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';

import rentHome from '../../../../../images/rent-home.svg';

import { RMWContext } from '../../../../../providers/RMWContext';

import { useSelector } from '../../../../../selectors/useSelector';

import SelectAvailableActionsCSEvents from '../../../../../model/ClickstreamEvent/SelectAvailableActionsCSEvents';

import SpinnerContainer from '../../../../SpinnerContainer';

import { actionCardStyles, sectionFlexStyles } from './styles';

import type {
  SelectAvailableActionsCSEventsProps,
  SelectAvailableActionsTriggerObject,
} from '../../../../../model/ClickstreamEvent/SelectAvailableActionsCSEvents';

type Props = {
  listingAlias: string;
  triggerObject?: SelectAvailableActionsTriggerObject;
};

const SelectAvailableActions = ({ listingAlias, triggerObject = 'posting_path' }: Props) => {
  const { paths } = useContext(RMWContext);

  const { user } = useSelector((state) => ({ app: state.app, user: state.user }), shallowEqual);

  const { data: webAnalyticsData, isLoading: isUseWebAnalyticsLoading } =
    useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
      {
        listingAlias,
        includeLeases: false,
      },
      { enabled: Boolean(listingAlias) },
    );

  const csEventParams = useMemo(() => {
    if (!webAnalyticsData || Object.keys(webAnalyticsData).length === 0) {
      logError({
        error: 'webAnalyticsData is empty:',
        errorType: 'SelectAvailableActions',
        context: `SelectAvailableActions@csEventParams. listingAlias: ${listingAlias}`,
      });
    }

    return { currentProperty: webAnalyticsData ?? {}, user, triggerObject };
  }, [webAnalyticsData, user, triggerObject]);

  const topMostElementRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    topMostElementRef?.current?.scrollIntoView(false);
  }, []);

  const handleLinkCreateListingClick = () => {
    SelectAvailableActionsCSEvents.CreateListing(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  const handleLinkApplicationClick = () => {
    SelectAvailableActionsCSEvents.InviteToApply(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  const handleLinkLeaseClick = () => {
    SelectAvailableActionsCSEvents.AddLease(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  const handleLinkPaymentClick = () => {
    SelectAvailableActionsCSEvents.SetUpPayments(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  return (
    <>
      <Image
        ref={topMostElementRef}
        src={rentHome}
        alt={'rent home'}
        css={{
          display: 'block',
          marginInline: 'auto',
          paddingBottom: '400',
          width: { lg: '60%' },
        }}
      />

      {isUseWebAnalyticsLoading ? (
        <SpinnerContainer />
      ) : (
        <>
          <Heading level={4} css={{ textAlign: 'center', paddingBottom: { lg: '100' } }}>
            You added your property
          </Heading>
          <Paragraph css={{ textAlign: 'center', marginBottom: '800' }}>
            You’re one step closer to listing on our networks
            <br />
            with over 1 million daily visitors across Zillow, Trulia, and HotPads.
          </Paragraph>
          <Upsell
            action={
              <TextButton asChild>
                <a href={`${paths.propertiesPage}/${listingAlias}/${paths.propertyDetailsListing}`}>
                  Create listing
                </a>{' '}
              </TextButton>
            }
            body={<Paragraph>It’s free to post and takes just a few minutes!</Paragraph>}
            header={<Heading level={4}>You’re one step closer to listing on our networks</Heading>}
            media={<Icon render={<Icon render={<IconYardSignFilled />} />} />}
            tone="impact"
            elevated={false}
            closeable={false}
            css={{
              // Padding is for blue section
              paddingBlock: { base: '600', lg: '800' },
              paddingInline: '400',
              // Margins is to make gaps around the blue section
              marginInline: { lg: '1800', xl: '2400' },
              marginBottom: { base: '400', lg: '600' },
            }}
          />
          <Box
            css={{
              display: 'flex',
              flexDirection: { base: 'column', lg: 'row' },
              alignItems: 'stretch',
              justifyContent: 'space-between',
              gap: { base: '400', lg: '600' },
              marginInline: { lg: '1800', xl: '2400' },
              marginBottom: { lg: '1200' },
            }}
          >
            <Card elevated={false} css={actionCardStyles}>
              <Box css={sectionFlexStyles}>
                <Icon size={{ base: 'xl', md: 'lg' }} render={<IconMailFilled />} />
                <div>
                  <Text
                    asChild
                    css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}
                  >
                    <p>Screen renters with our hassle-free applications</p>
                  </Text>
                  <TextButton
                    asChild
                    css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                    onClick={handleLinkApplicationClick}
                    data-eventid={SelectAvailableActionsCSEvents.InviteToApply.data_event_id}
                  >
                    <a href={`${paths.base}/${paths.propertyDetailsApplications}?propertyId=${listingAlias}`}>
                      Invite someone to apply
                    </a>
                  </TextButton>
                </div>
              </Box>
            </Card>
            <Card elevated={false} css={actionCardStyles}>
              <Box css={sectionFlexStyles}>
                <Icon size={{ base: 'xl', md: 'lg' }} render={<IconHandshakeFilled />} />
                <div>
                  <Text
                    asChild
                    css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}
                  >
                    <p>Upload documents or create a custom lease for signing</p>
                  </Text>
                  <TextButton
                    asChild
                    css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                    onClick={handleLinkLeaseClick}
                    data-eventid={SelectAvailableActionsCSEvents.AddLease.data_event_id}
                  >
                    <a href={`${paths.leasing}/property/${listingAlias}`}>Add a lease </a>
                  </TextButton>
                </div>
              </Box>
            </Card>
            <Card elevated={false} css={actionCardStyles}>
              <Box css={sectionFlexStyles}>
                <Icon size={{ base: 'xl', md: 'lg' }} render={<IconPiggyBankFilled />} />
                <div>
                  <Text
                    asChild
                    css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}
                  >
                    <p>Collect rent payments at no cost to you</p>
                  </Text>
                  <TextButton
                    asChild
                    css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                    onClick={handleLinkPaymentClick}
                    data-eventid={SelectAvailableActionsCSEvents.SetUpPayments.data_event_id}
                  >
                    <a href={`${paths.base}/${paths.propertyDetailsPayments}/${paths.properties}/${listingAlias}`}>
                      Set up payments
                    </a>
                  </TextButton>
                </div>
              </Box>
            </Card>
          </Box>
        </>
      )}
    </>
  );
};

export default SelectAvailableActions;
